// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#logs
// Monitoring tab log viewer: a live SSE follow of `logs/stream`, falling back to
// polling `api.logs.tail` cursor-forward from the last streamed line when the
// stream drops or the browser has no EventSource. The viewer follows the
// owner directive: audit-log-style grouped rows (newest at top), level pill,
// mono message wrapping, Filter/Since server query params preserved, Copy in
// chronological order, entrance animation (reduced-motion safe), 5k buffer.
import * as React from 'react';
import { Check, Copy, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { type AnsiSpan, parseAnsi, stripAnsi } from '@/lib/ansi';
import { AD_GROUP } from '@/components/greenlight/surfaces';
import { api } from '@/lib/api';
import { copyTextToClipboard } from '@/lib/clipboard';
import { cn } from '@/lib/utils';
import type { LogRow, LogStreamEndEvent, LogStreamLineEvent } from '@greenlight/shared';

const POLL_MS = 4_000;
const PAGE_LIMIT = 500;
// After the stream drops, poll for this long before trying the stream again.
const STREAM_RETRY_MS = 30_000;
// Streamed lines are applied in batches so a history burst renders once.
const STREAM_FLUSH_MS = 100;
// Cap the in-memory buffer so a chatty pod can't grow the tab unbounded.
const MAX_LINES = 5_000;

// Relative "since" windows, wired to the contract's relative-time form.
const SINCE_PRESETS = ['5m', '15m', '1h', '6h', '24h'] as const;
type Since = (typeof SINCE_PRESETS)[number];
const DEFAULT_SINCE: Since = '15m';

const SINCE_LABELS: Record<Since, string> = {
  '5m': 'Last 5 minutes',
  '15m': 'Last 15 minutes',
  '1h': 'Last hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
};

// Log level detection from the message prefix. Only the levels the existing
// ANSI parser + docs/11 surface are shown. Unrecognised prefix → no pill.
type LogLevel = 'INFO' | 'WARN' | 'ERROR';

function detectLevel(message: string): LogLevel | null {
  const upper = stripAnsi(message).trimStart().toUpperCase();
  if (upper.startsWith('ERROR')) return 'ERROR';
  if (upper.startsWith('WARN')) return 'WARN';
  if (upper.startsWith('INFO')) return 'INFO';
  return null;
}

// Level pill classes — status tokens, color is never the only signal (the
// label text carries the same information).
const LEVEL_CLASSES: Record<LogLevel, string> = {
  INFO: 'bg-[hsl(var(--status-green)/0.12)] text-[hsl(var(--status-green-text))]',
  WARN: 'bg-[hsl(var(--status-amber)/0.12)] text-[hsl(var(--status-amber-text))]',
  ERROR: 'bg-[hsl(var(--status-red)/0.12)] text-[hsl(var(--status-red-text))]',
};

function spanClass(span: AnsiSpan): string {
  if (!span.fg) return '';
  const FG: Record<string, string> = {
    red: 'text-[hsl(var(--status-red-text))]',
    green: 'text-[hsl(var(--status-green-text))]',
    yellow: 'text-[hsl(var(--status-amber-text))]',
    blue: 'text-link',
    magenta: 'text-indigo-ink',
    cyan: 'text-link',
    white: 'text-foreground',
    gray: 'text-[hsl(var(--text-tertiary))]',
  };
  return cn(FG[span.fg], span.bold && 'font-semibold');
}

// Stable identity for a log line, for dedupe across overlapping poll windows.
function lineKey(row: LogRow): string {
  return JSON.stringify([row.timestamp, row.message, row.pod ?? '']);
}

type Transport = 'stream' | 'poll';

interface TailState {
  readonly lines: readonly LogRow[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly transport: Transport;
}

interface InternalState {
  id: string;
  cursor: string | null;
  lines: LogRow[];
  transport: Transport;
}

function initialTransport(): Transport {
  return typeof EventSource === 'undefined' ? 'poll' : 'stream';
}

// Append rows not already in the buffer tail, capped at MAX_LINES.
function appendLines(lines: LogRow[], rows: readonly LogRow[]): LogRow[] {
  if (rows.length === 0) return lines;
  const seen = new Set(lines.slice(-PAGE_LIMIT).map(lineKey));
  const fresh = rows.filter((row) => !seen.has(lineKey(row)));
  if (fresh.length === 0) return lines;
  const merged = lines.concat(fresh);
  return merged.length > MAX_LINES ? merged.slice(merged.length - MAX_LINES) : merged;
}

/**
 * Follows the app's logs over SSE and accumulates lines. One cursor is shared
 * by both transports: each streamed batch advances it to its last line, so the
 * polling fallback resumes exactly there, and a reconnecting stream resumes
 * from wherever polling got to. Pausing closes the stream and stops the poll.
 * Changing filter/since starts a fresh tail.
 */
function useLogTail(
  appId: string,
  opts: { filter: string; since: Since; paused: boolean },
): TailState {
  const { filter, since, paused } = opts;
  const tailId = JSON.stringify([appId, filter, since]);
  const [state, setState] = React.useState<InternalState>(() => ({
    id: tailId,
    cursor: null,
    lines: [],
    transport: initialTransport(),
  }));
  const [streamOpen, setStreamOpen] = React.useState(false);
  // Bumped to reopen the stream after a server-side rotation.
  const [generation, setGeneration] = React.useState(0);

  // Reset synchronously when the tail identity changes, so the next fetch can't
  // read a stale cursor (the React "adjust state during render" pattern).
  if (state.id !== tailId) {
    setState({ id: tailId, cursor: null, lines: [], transport: initialTransport() });
  }

  const cursorRef = React.useRef(state.cursor);
  cursorRef.current = state.cursor;
  const transport = state.transport;
  const setTransport = React.useCallback(
    (next: Transport) =>
      setState((prev) =>
        prev.id !== tailId || prev.transport === next ? prev : { ...prev, transport: next },
      ),
    [tailId],
  );

  React.useEffect(() => {
    if (paused || transport !== 'stream') return;
    const qs = new URLSearchParams();
    if (cursorRef.current) qs.set('cursor', cursorRef.current);
    else qs.set('since', since);
    if (filter) qs.set('filter', filter);
    const source = new EventSource(`/api/apps/${encodeURIComponent(appId)}/logs/stream?${qs}`, {
      withCredentials: true,
    });
    let batch: LogStreamLineEvent[] = [];
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    const flush = () => {
      flushTimer = undefined;
      const events = batch;
      batch = [];
      const last = events.at(-1);
      if (!last) return;
      setState((prev) =>
        prev.id !== tailId
          ? prev
          : {
              ...prev,
              lines: appendLines(
                prev.lines,
                events.map((e) => e.row),
              ),
              cursor: last.cursor,
            },
      );
    };
    // Lines and cursor advance together, so dropping an unflushed batch on
    // close only means the next reader re-reads it.
    const close = () => {
      source.close();
      clearTimeout(flushTimer);
      batch = [];
      setStreamOpen(false);
    };
    source.addEventListener('open', () => setStreamOpen(true));
    source.addEventListener('line', (e) => {
      batch.push(JSON.parse((e as MessageEvent<string>).data) as LogStreamLineEvent);
      flushTimer ??= setTimeout(flush, STREAM_FLUSH_MS);
    });
    source.addEventListener('end', (e) => {
      const { reason } = JSON.parse((e as MessageEvent<string>).data) as LogStreamEndEvent;
      flush();
      close();
      if (reason === 'rotate') setGeneration((g) => g + 1);
      else setTransport('poll');
    });
    source.onerror = () => {
      close();
      setTransport('poll');
    };
    return close;
  }, [appId, filter, since, paused, transport, tailId, generation, setTransport]);

  React.useEffect(() => {
    if (paused || transport !== 'poll' || typeof EventSource === 'undefined') return;
    const t = setTimeout(() => setTransport('stream'), STREAM_RETRY_MS);
    return () => clearTimeout(t);
  }, [paused, transport, setTransport]);

  const polling = transport === 'poll';
  const key = ['logs.tail', appId, filter, since] as const;
  const res = api.logs.tail.useQuery(
    key,
    {
      params: { id: appId },
      query: {
        cursor: state.cursor ?? undefined,
        filter: filter || undefined,
        since,
        limit: PAGE_LIMIT,
      },
    },
    {
      queryKey: key,
      enabled: polling,
      refetchInterval: paused || !polling ? false : POLL_MS,
      refetchOnWindowFocus: false,
    },
  );

  // Append each fetched page exactly once — react-query yields a new object per
  // fetch, so identity gates the append.
  const lastSeen = React.useRef<unknown>(null);
  // A fresh tail (filter/since/app changed) must not be blocked by the previous
  // tail's last-seen identity, so clear the guard alongside the buffer reset.
  React.useEffect(() => {
    lastSeen.current = null;
  }, [tailId]);
  React.useEffect(() => {
    if (res.data?.status !== 200) return;
    if (res.data === lastSeen.current) return;
    lastSeen.current = res.data;
    // A poll that lands after the switch to streaming is already covered by the
    // stream, which reads from its own cursor.
    if (!polling) return;
    const page = res.data.body;
    setState((prev) => {
      if (prev.id !== tailId) return prev; // a page from a superseded tail
      const lines = appendLines(prev.lines, page.items);
      const cursor = page.next_cursor ?? prev.cursor;
      if (lines === prev.lines && cursor === prev.cursor) return prev;
      return { ...prev, cursor, lines };
    });
  }, [res.data, tailId, polling]);

  return {
    lines: state.lines,
    isLoading: state.lines.length === 0 && (polling ? res.isLoading : !streamOpen && !paused),
    isError: polling && res.isError,
    transport,
  };
}

function formatTs(ts: string): { rel: string; abs: string } {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return { rel: ts, abs: ts };
  // Relative: HH:MM:SS.mmm — concise mono timestamp a la log readers
  const abs = d.toLocaleTimeString(undefined, { hour12: false, timeStyle: 'medium' });
  // Also provide a fractional seconds version for the tooltip
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  return { rel: `${abs}.${ms}`, abs: d.toISOString() };
}

interface LogRowProps {
  row: LogRow;
  isNew: boolean;
}

// A single audit-log-style row: absolute timestamp (mono), level pill, mono message.
// The entrance animation (slide-in + fade) fires only for newly-polled lines
// and respects prefers-reduced-motion via the CSS class — the animation is
// defined as a no-op under @media (prefers-reduced-motion: reduce) in globals.css.
function LogRowItem({ row, isNew }: LogRowProps): React.ReactElement {
  const level = detectLevel(row.message);
  const spans = parseAnsi(row.message);
  const ts = formatTs(row.timestamp);

  return (
    <div
      className={cn(
        'flex flex-wrap items-start gap-x-2 gap-y-0.5 px-[18px] py-2.5',
        'border-t border-border/50 first:border-t-0',
        isNew && 'gl-log-row-enter',
      )}
    >
      {/* Timestamp — mono, muted, shrinks on narrow screens */}
      <time
        dateTime={row.timestamp}
        className="shrink-0 font-mono text-[11.5px] text-[hsl(var(--text-tertiary))] tabular-nums"
        title={ts.abs}
      >
        {ts.rel}
      </time>

      {/* Level pill — only when a level is detected; color is never the only signal */}
      {level !== null && (
        <span
          className={cn(
            'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase',
            LEVEL_CLASSES[level],
          )}
          aria-label={`log level ${level}`}
        >
          {level}
        </span>
      )}

      {/* Message — mono 12.5px, wrapping */}
      <span className="min-w-0 flex-1 break-words font-mono text-[12.5px] leading-[1.45] text-foreground">
        {spans.map((span, i) => (
          <span key={i} className={spanClass(span)}>
            {span.text}
          </span>
        ))}
      </span>
    </div>
  );
}

export function LogsTab({ appId }: { appId: string }): React.JSX.Element {
  const [filterInput, setFilterInput] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const [since, setSince] = React.useState<Since>(DEFAULT_SINCE);
  const [paused, setPaused] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1_500);
    return () => clearTimeout(timer);
  }, [copied]);

  // Debounce the filter so each keystroke doesn't reset the tail.
  React.useEffect(() => {
    const t = setTimeout(() => setFilter(filterInput.trim()), 300);
    return () => clearTimeout(t);
  }, [filterInput]);

  const { lines, isLoading, isError, transport } = useLogTail(appId, { filter, since, paused });

  // Track which lines are "new" (from the most recent poll) to drive the entrance
  // animation. null = baseline not yet established; treat as no-new-keys so the
  // initial populate (and any repopulate after a filter/since reset) doesn't
  // animate the entire buffer.
  const prevKeysRef = React.useRef<Set<string> | null>(null);
  const newKeys = React.useMemo(() => {
    if (prevKeysRef.current === null) return new Set<string>();
    const current = new Set(lines.map(lineKey));
    const fresh = new Set<string>();
    for (const k of current) {
      if (!prevKeysRef.current.has(k)) fresh.add(k);
    }
    return fresh;
  }, [lines]);
  // Update the ref after computing new keys (not during render — only update
  // after we've had a chance to identify fresh ones).
  React.useEffect(() => {
    prevKeysRef.current = new Set(lines.map(lineKey));
  }, [lines]);
  // When the tail identity resets (filter/since/app change), reset to null so
  // the repopulated buffer is treated as a fresh baseline with no animation.
  React.useEffect(() => {
    prevKeysRef.current = null;
  }, [filter, since, appId]);

  // Copy copies lines in chronological order (oldest first) as the canonical
  // log text. Note: the display renders newest-at-top, but copy is temporal order
  // so the plain-text output reads like a log file.
  const copyLines = React.useCallback(async () => {
    const text = [...lines]
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      .map((l) => `${l.timestamp} ${stripAnsi(l.message)}`)
      .join('\n');
    if (await copyTextToClipboard(text)) {
      setCopied(true);
      toast.success('Log lines copied.');
    } else {
      toast.error("Couldn't copy log lines.");
    }
  }, [lines]);

  // Display newest-first: reverse the accumulated buffer for rendering.
  const displayLines = React.useMemo(() => [...lines].reverse(), [lines]);

  return (
    <div className="space-y-3" data-testid="logs-tab">
      {/* Toolbar: Filter + Since + Pause */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="log-filter" className="text-xs">
            Filter
          </Label>
          <Input
            id="log-filter"
            value={filterInput}
            onChange={(e) => setFilterInput(e.target.value)}
            placeholder="Filter log lines"
            className="h-9 w-48 rounded-full"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="log-since" className="text-xs">
            Since
          </Label>
          <Select value={since} onValueChange={(v) => setSince(v as Since)}>
            <SelectTrigger id="log-since" className="h-9 w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SINCE_PRESETS.map((p) => (
                <SelectItem key={p} value={p}>
                  {SINCE_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="mb-0 h-9 w-9 self-end rounded-full"
                aria-label={paused ? 'Resume log tail' : 'Pause log tail'}
                aria-pressed={paused}
                onClick={() => setPaused((p) => !p)}
                data-testid="logs-pause-btn"
              >
                {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{paused ? 'Resume' : 'Pause'}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {isLoading ? (
        <div className="space-y-2" data-testid="logs-loading">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : lines.length === 0 ? (
        // Only blank to the error alert when nothing is buffered. A failed
        // *refresh* after lines have loaded keeps the tail visible — the
        // poll keeps retrying, so a transient blip shouldn't wipe the logs.
        isError ? (
          <Alert variant="destructive" data-testid="logs-error">
            <AlertTitle>Couldn't load logs</AlertTitle>
            <AlertDescription>
              The log tail is unavailable right now. Retry in a moment.
            </AlertDescription>
          </Alert>
        ) : (
          <div
            className="rounded-gl-icon border border-border p-6 text-center text-sm text-muted-foreground"
            data-testid="logs-empty"
          >
            No logs in this range
          </div>
        )
      ) : (
        <TooltipProvider>
          {isError && (
            <p
              role="status"
              data-testid="logs-stale"
              className="mb-2 text-xs text-[hsl(var(--status-amber-text))]"
            >
              Couldn't refresh just now — retrying. Showing the lines received so far.
            </p>
          )}
          <div
            data-paused={paused ? 'true' : 'false'}
            data-transport={transport}
            data-testid="logs-viewer"
          >
            {/* Panel header: line count + Copy */}
            <div className="flex items-center justify-between px-[18px] py-3 border-b border-border/60">
              <span className="font-mono text-[11px] text-[hsl(var(--text-tertiary))] uppercase tracking-[0.08em]">
                stdout · stderr{' '}
                <span className="normal-case">
                  · last {lines.length.toLocaleString()} line{lines.length === 1 ? '' : 's'}
                  {!paused && (
                    <span data-testid="logs-transport">
                      {transport === 'stream' ? ' · live' : ' · refreshing every few seconds'}
                    </span>
                  )}
                </span>
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Copy log lines"
                    onClick={copyLines}
                    className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                    data-testid="logs-copy-btn"
                  >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    Copy
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Copy lines (chronological order)</TooltipContent>
              </Tooltip>
            </div>

            {/* Log rows — newest at top, fixed height so 5k-line buffers don't
                  blow out the tab height. */}
            <ScrollArea className="h-[480px]">
              <div
                data-testid="logs-output"
                className={cn(
                  AD_GROUP,
                  'whitespace-pre-wrap break-words divide-y divide-border/30',
                )}
              >
                {displayLines.map((l) => (
                  <LogRowItem key={lineKey(l)} row={l} isNew={newKeys.has(lineKey(l))} />
                ))}
              </div>
            </ScrollArea>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}
